import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";

export interface ActivityModalTab {
  key: string;
  label: string;
  content: React.ReactNode;
  /** Mostra un pallino rosso sul trigger (tab con campi in errore) */
  hasError?: boolean;
  /** Non renderizzata affatto (es. tab Immagini per Flight/Transportation) */
  hidden?: boolean;
}

interface ActivityModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  icon?: React.ReactNode;
  title: React.ReactNode;
  formId: string;
  onSubmit: (e: React.FormEvent) => void;
  showValidationBanner: boolean;
  tabs: ActivityModalTab[];
  activeTab: string;
  onActiveTabChange: (key: string) => void;
}

/**
 * Chrome condiviso tra le modali attività del builder in creazione/modifica:
 * Modal + Tabs + footer con validation banner. Ogni tipo di attività resta
 * proprietario dei propri campi/validazione — passa solo il contenuto già
 * pronto per ogni tab. min-h-[65vh] su ogni TabsContent evita che il modale si
 * rimpicciolisca passando da un tab con molti campi a uno corto (es. Documenti).
 *
 * La sola lettura (apertura da Libreria) NON passa da qui: le modali fanno
 * early-return su <ActivityDetailView /> quando `readOnly` è attivo.
 */
const ActivityModalShell = ({
  isOpen,
  onClose,
  icon,
  title,
  formId,
  onSubmit,
  showValidationBanner,
  tabs,
  activeTab,
  onActiveTabChange,
}: ActivityModalShellProps) => {
  const { t } = useI18n();
  const visibleTabs = tabs.filter(tab => !tab.hidden);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          {icon} {title}
        </div>
      }
      size="lg"
      footer={
        <div className="flex flex-col w-full gap-2">
          {showValidationBanner && (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {t('modals.requiredFieldsError')}
            </div>
          )}
          <div className="flex w-full justify-between">
            <Button type="button" size="sm" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" form={formId}>
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="p-6 pt-2">
        <Tabs value={activeTab} onValueChange={onActiveTabChange}>
          <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-gray-200 bg-transparent p-0">
            {visibleTabs.map(tab => (
              <TabsTrigger
                key={tab.key}
                type="button"
                value={tab.key}
                className="gap-1.5 rounded-none border-b-2 border-transparent bg-transparent px-1 pb-3 text-gray-500 shadow-none transition-colors hover:text-gray-700 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-none"
              >
                {tab.label}
                {tab.hasError && <span className="h-1.5 w-1.5 rounded-full bg-red-500" />}
              </TabsTrigger>
            ))}
          </TabsList>

          {visibleTabs.map(tab => (
            <TabsContent
              key={tab.key}
              value={tab.key}
              className="min-h-[65vh] space-y-4 pt-4"
            >
              {tab.content}
            </TabsContent>
          ))}
        </Tabs>
      </form>
    </Modal>
  );
};

export default ActivityModalShell;
